import { useCallback, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import css from './AppShell.module.css';

/** The body class set while a handle is dragged (see .resizing). */
const RESIZING = css.resizing ?? 'resizing';

/* The width of a side panel the operator can drag (the rail on the left, the
 * SOS column on the right), remembered per browser. Storage can be missing or
 * throw (a private window, blocked site data), and then the panel simply
 * starts at its default — a width is a convenience, never a setting. */
export function usePanelWidth(key: string, fallback: number, min: number, max: number) {
  const clamp = useCallback((n: number) => Math.round(Math.min(max, Math.max(min, n))), [min, max]);
  const [width, setWidthState] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(key));
      return saved ? clamp(saved) : fallback;
    } catch {
      return fallback;
    }
  });
  const setWidth = useCallback(
    (n: number) => {
      const next = clamp(n);
      setWidthState(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        /* not remembered; still applied */
      }
    },
    [key, clamp],
  );
  return { width, setWidth, reset: () => setWidth(fallback) };
}

/* The drag strip on a panel's inner edge. `edge` is which side of the PAGE the
 * panel sits on: dragging toward the page widens a left panel and narrows a
 * right one. Arrow keys move it 16 px, so it works without a mouse, and a
 * double-click puts the default back. */
export function ResizeHandle({
  edge,
  width,
  setWidth,
  reset,
  label,
}: {
  edge: 'left' | 'right';
  width: number;
  setWidth: (n: number) => void;
  reset: () => void;
  label: string;
}) {
  const sign = edge === 'left' ? 1 : -1;

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    document.body.classList.add(RESIZING);
    const move = (ev: globalThis.PointerEvent) => setWidth(startW + sign * (ev.clientX - startX));
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      document.body.classList.remove(RESIZING);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') setWidth(width - sign * 16);
    else if (e.key === 'ArrowRight') setWidth(width + sign * 16);
    else return;
    e.preventDefault();
  };

  return (
    <div
      className={css.resizeHandle}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      tabIndex={0}
      title={`${label} — drag to resize, double-click to reset`}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={reset}
    />
  );
}
